import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import EnrollmentService from "../../../_services/EnrollmentService";

interface ReverseDiscountModalProps {
  show: boolean;
  onHide: () => void;
  enrollmentId: number;
  discount: any; // the discount transaction being reversed
  onSuccess: () => void;
}

const ReverseDiscountModal: React.FC<ReverseDiscountModalProps> = ({
  show,
  onHide,
  enrollmentId,
  discount,
  onSuccess,
}) => {
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  useEffect(() => {
    if (show) {
      setError(null);
      setSuccessMsg(null);
      setRemarks("");
    }
  }, [show]);

  const handleSubmit = async () => {
    setError(null);
    if (!remarks.trim()) {
      setError("Remarks are required for reversal");
      return;
    }
    setSubmitting(true);
    try {
      const res = await EnrollmentService.reverseDiscount(enrollmentId, {
        paymentId: discount.paymentId,
        remarks: remarks.trim(),
      });
      if (res.data?.success) {
        setSuccessMsg("Discount reversed successfully");
        onSuccess();
      } else {
        setError(res.data?.message || "Failed to reverse discount");
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to reverse discount",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>Reverse Discount</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ fontSize: 13 }}>
        {error && (
          <Alert variant="danger" style={{ fontSize: 12 }}>
            {error}
          </Alert>
        )}
        {successMsg && (
          <Alert variant="success" style={{ fontSize: 12 }}>
            {successMsg}
          </Alert>
        )}

        {discount && (
          <div
            style={{
              background: "var(--surface-page)",
              padding: 10,
              borderRadius: 4,
              marginBottom: 12,
              fontSize: 12,
            }}
          >
            <div>
              <strong>Date:</strong> {discount.paymentDate}
            </div>
            <div>
              <strong>Amount (Before GST):</strong>{" "}
              {discount.amount?.toLocaleString()}
            </div>
            <div>
              <strong>Discount Category:</strong> {discount.discountCategory}
            </div>
          </div>
        )}

        <Form.Group className="mb-2">
          <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>
            Remarks *
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            size="sm"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Enter reason for reversing this discount..."
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        {successMsg ? (
          <Button variant="secondary" size="sm" onClick={onHide}>
            Close
          </Button>
        ) : (
          <>
            <Button variant="secondary" size="sm" onClick={onHide}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Spinner role="status" animation="border" size="sm" />{" "}
                  Reversing...
                </>
              ) : (
                "Reverse Discount"
              )}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ReverseDiscountModal;
